/**
 * screens/modals/CategoryModal
 * Add a spend category: a code, a name, and the GL account it maps to.
 *
 * The code is what appears on every ledger row that uses this category, in
 * mono — so it is uppercased on save and kept short. The GL hint is optional
 * here because not every org has decided its chart of accounts by the time it
 * starts recording petty cash.
 */
import { useState } from 'react';
import { Input, Modal } from '../../components';
import { createCategory } from '../../services/config/config.service';
import { useConfig } from '../../context/ConfigContext';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export function CategoryModal() {
  const { closeModal } = useShell();
  const { reload, categories } = useConfig();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [gl, setGl] = useState('');

  const { submitting, run } = useModalSubmit({
    validate: () => {
      const trimmed = code.trim().toUpperCase();
      if (!trimmed) return 'Give the category a code.';
      if (!name.trim()) return 'Give the category a name.';
      // Caught here so the user is not told by a 409 what a glance could tell
      // them. A deactivated category still holds its code, hence no is_active
      // filter.
      if (categories.some((category) => category.code.toUpperCase() === trimmed)) {
        return `${trimmed} already exists.`;
      }
      return null;
    },
    submit: () =>
      createCategory({
        code: code.trim().toUpperCase(),
        display_name: name.trim(),
        ...(gl.trim() ? { gl_hint: gl.trim() } : {}),
      }),
    title: 'Category added',
    fallback: 'It is now offered on the spend form.',
    onDone: () => {
      // Categories live in the bootstrap payload, so the workspace is reloaded
      // rather than this modal patching a local copy.
      void reload();
    },
  });

  return (
    <Modal
      visible
      title="Add a spend category"
      hint="Every spend carries one. Categories are deactivated, never deleted."
      onClose={closeModal}
      primaryLabel="Add category"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <Input
        label="Code"
        required
        hint="Shown on every ledger row that uses it. Kept short."
        value={code}
        onChangeText={setCode}
        placeholder="FUEL"
        monospace
        autoCapitalize="characters"
      />
      <Input label="Name" required value={name} onChangeText={setName} placeholder="Fuel and mileage" />
      <Input
        label="GL account"
        hint="Optional. The account this maps to in the general ledger."
        value={gl}
        onChangeText={setGl}
        placeholder="6120"
        monospace
      />
    </Modal>
  );
}

export default CategoryModal;
